import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MobileContainer, Card } from '../theme/GlobalStyles';
import * as Icons from 'react-icons/fa';
import CustomerHeader from '../components/CustomerHeader';
import CustomAlert from '../components/CustomAlert';
import StatusTrackingPopup from '../components/StatusTrackingPopup';
import TrackingHistoryModal from '../components/TrackingHistoryModal';
import ticketService from '../services/ticketService';
import dynamicEntityService from '../services/dynamicEntityService';
import RaiseComplaintIcon from '../assets/images/RaiseComplaintIcon.png';
import cdeFountain from '../assets/images/Fountain.jpg';
import cdeCooler from '../assets/images/Fridge.jpeg';

const FaTools = Icons.FaTools as any;
const FaExclamationTriangle = Icons.FaExclamationTriangle as any;
const FaCheckCircle = Icons.FaCheckCircle as any;
const FaSearch = Icons.FaSearch as any;
const FaInfo = Icons.FaInfo as any;
const FaChartBar = Icons.FaChartBar as any;
const FaQrcode = Icons.FaQrcode as any;
const FaHistory = Icons.FaHistory as any;
const FaTimes = Icons.FaTimes as any;
const FaCopy = Icons.FaCopy as any;
const FaTimesCircle = Icons.FaTimesCircle as any;
const FaClock = Icons.FaClock as any;
const FaArrowRight = Icons.FaArrowRight as any;
const FaChevronLeft = Icons.FaChevronLeft as any;
const FaChevronRight = Icons.FaChevronRight as any;

interface CDEUnit {
  id: string;
  serialNumber: string;
  manufacturerSerial: string;
  model: string;
  type: string;
  cdeImage: string;
  description: string;
  status: string;
  isVerified: number;
  verificationStatus: string;
}

const cdeImages: Record<string, string> = {
  'Fountain': cdeFountain,
  'Cooler': cdeCooler,
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled(MobileContainer)`
  padding-top: 1rem;
`;

// --- CDE Slider Styled Components ---
const SliderSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SliderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const SliderTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  font-size: 1rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SliderWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
`;

const SliderTrack = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'offset'
})<{ offset: number }>`
  display: flex;
  transition: transform 0.4s ease;
  transform: translateX(-${props => props.offset * 100}%);
`;

const SliderCard = styled.div`
  min-width: 100%;
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s;

  &:active {
    border-color: ${props => props.theme.primary};
  }
`;

const SliderCDEImage = styled.img`
  width: 70px;
  height: 70px;
  object-fit: contain;
  border-radius: 8px;
  background: #f8f9fa;
  padding: 4px;
  flex-shrink: 0;
`;

const SliderCDEInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SliderCDEType = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.theme.primary};
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`;

const SliderCDEDesc = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SliderCDESerial = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
  margin-top: 0.15rem;
`;

const SliderBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'verificationStatus'
})<{ verificationStatus: number }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-top: 0.35rem;
  color: white;

  ${props => {
    if (props.verificationStatus === 1) return 'background: #4caf50;';
    if (props.verificationStatus === 2) return 'background: #2196f3;';
    return 'background: #ff9800;';
  }}

  svg { font-size: 0.7rem; }
`;

const SliderNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const SliderDot = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? props.theme.primary : '#ccc'};
  transform: ${props => props.active ? 'scale(1.3)' : 'scale(1)'};
`;

const SliderArrowBtn = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.7rem;
  padding: 0;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const MenuItem = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  min-height: 120px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.theme.surface};
  border: 2px solid ${props => props.theme.border};
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(244, 0, 0, 0.15);
    border-color: ${props => props.theme.primary};
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const DemoBadge = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background:rgb(54, 55, 56);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(14, 9, 9, 0.2);
`;

const MenuIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.theme.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuIconImage = styled.img`
  width: 3rem;
  height: 3rem;
  object-fit: contain;
`;

const MenuTitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  color: ${props => props.theme.textPrimary};
  line-height: 1.2;
`;

const TicketHistoryPreview = styled(Card)`
  margin-top: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  margin-bottom: 1rem;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TicketItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'status'
})<{ status: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  border-left: 4px solid ${props => props.status === 'Completed' ? '#28a745' : props.status === 'Pending' ? '#ffc107' : '#dc3545'};
`;

const TicketInfo = styled.div`
  flex: 1;
`;

const TicketId = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${props => props.theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CountBadge = styled.span`
  background: ${props => props.theme.primary};
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    background: ${props => props.theme.primaryDark || '#c00000'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TicketType = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: ${props => props.theme.textSecondary};
`;

const TicketStatus = styled.span<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  background: ${props =>
    props.status === 'Completed' ? '#28a745' :
    props.status === 'Pending' ? '#ffc107' :
    '#dc3545'
  };
`;

const AssignedRole = styled.p`
  color: ${props => props.theme.primary};
  font-size: 0.75rem;
  margin: 0.25rem 0 0;
  font-weight: 500;
  background: rgba(244, 0, 0, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  display: inline-block;
`;

const LoginIdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const LoginIdLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 0.7rem;
  font-weight: 500;
`;

const LoginIdValue = styled.span`
  color: ${props => props.theme.textPrimary};
  font-size: 0.7rem;
  font-family: monospace;
  background: rgba(0, 0, 0, 0.05);
  padding: 0.15rem 0.35rem;
  border-radius: 3px;
`;

const CopyLoginIdButton = styled.button`
  background: white;
  border: 1px solid ${props => props.theme.primary};
  color: ${props => props.theme.primary};
  cursor: pointer;
  padding: 0.15rem 0.35rem;
  border-radius: 3px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6rem;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TicketActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const TrackHistoryBtn = styled.button`
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  border: none;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
    transform: translateY(-1px);
  }
`;

const DuplicatesModal = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 1rem;
`;

const DuplicatesContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const DuplicatesHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 2px solid ${props => props.theme.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const DuplicatesTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.primary};
`;

const DuplicatesCloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }
`;

const DuplicatesBody = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
`;

const DuplicateItem = styled.div`
  background: ${props => props.theme.surface || '#f5f5f5'};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.theme.border};

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const DuplicateItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const DuplicateItemTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  font-size: 0.9rem;
`;

const DuplicateItemDate = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const DuplicateItemType = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const CustomerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [recentTickets, setRecentTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
  }>({ isVisible: false, message: '' });
  const [statusTrackingPopup, setStatusTrackingPopup] = useState<{
    isVisible: boolean;
    formName: string;
    currentStatus: string;
    ticketNumber: string;
  }>({ isVisible: false, formName: '', currentStatus: '', ticketNumber: '' });
  const [trackingHistoryModal, setTrackingHistoryModal] = useState<{
    isVisible: boolean;
    recordUid: string;
    ticketNumber: string;
  }>({ isVisible: false, recordUid: '', ticketNumber: '' });
  const [duplicateTicketsModal, setDuplicateTicketsModal] = useState<{
    isVisible: boolean;
    tickets: any[];
    ticketNumber: string;
  }>({ isVisible: false, tickets: [], ticketNumber: '' });

  const [copiedLoginId, setCopiedLoginId] = useState<string | null>(null);

  // CDE Slider state
  const [cdeUnits, setCdeUnits] = useState<CDEUnit[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const copyLoginId = (loginId: string, ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(loginId);
    setCopiedLoginId(ticketId);
    setTimeout(() => setCopiedLoginId(null), 2000);
  };

  // Check for customer code on component mount
  useEffect(() => {
    const checkCustomerInfo = () => {
      const storedCustomerData = localStorage.getItem('customerData');
      let customerCode = '';
      let customerName = '';

      if (storedCustomerData) {
        try {
          const parsedData = JSON.parse(storedCustomerData);
          customerCode = parsedData.code || parsedData.Code || parsedData.number || parsedData.Number || '';
          customerName = parsedData.name || parsedData.Name || '';
        } catch (e) {
          // If parsing fails, check direct localStorage values
          customerCode = localStorage.getItem('customerCode') || '';
          customerName = localStorage.getItem('customerName') || '';
        }
      } else {
        customerCode = localStorage.getItem('customerCode') || '';
        customerName = localStorage.getItem('customerName') || '';
      }

      // If customer code or name is missing, navigate to scanner home screen
      if (!customerCode || !customerName) {
        navigate('/');
      } else {
        loadRecentTickets();
      }
    };

    checkCustomerInfo();
  }, []);

  // Show verification incomplete toast if redirected from login
  useEffect(() => {
    if (sessionStorage.getItem('verificationIncomplete') === 'true') {
      sessionStorage.removeItem('verificationIncomplete');
      setAlert({
        isVisible: true,
        type: 'info',
        title: 'Verification Incomplete',
        message: 'Your CDE verification is not yet complete.'
      });
    }
  }, []);

  // Load recent tickets on component mount
  useEffect(() => {
    const storedCustomerCode = localStorage.getItem('customerCode');
    if (storedCustomerCode) {
      loadRecentTickets();
    }
  }, []);

  // Load CDE units on mount
  useEffect(() => {
    const loadCDEUnits = async () => {
      try {
        const greenTag = localStorage.getItem('greenTagNumber') || localStorage.getItem('customerNumber');
        if (!greenTag) return;

        const response = await dynamicEntityService.getEntityData('verify_cde', greenTag, {});
        if (response.success && response.data) {
          const units: CDEUnit[] = response.data.map((item: any, index: number) => {
            let cdeType = 'Equipment';
            const rawType = item.object_type || item.material || item.cde_type || '';
            if (rawType) {
              const typeStr = rawType.toString().toLowerCase();
              if (typeStr.includes('fountain') || typeStr.includes('dispenser') || typeStr.includes('bib')) {
                cdeType = 'Fountain';
              } else if (typeStr.includes('cooler') || typeStr.includes('refrigerator') || typeStr.includes('chiller')) {
                cdeType = 'Cooler';
              } else {
                cdeType = rawType;
              }
            }
            return {
              id: item.id || String(index + 1),
              serialNumber: item.serial_no || '',
              manufacturerSerial: item.manufacturer_serial_no || item.manufact_serial_number || '',
              model: item.model_no || '',
              type: cdeType,
              cdeImage: cdeImages[cdeType] || cdeCooler,
              description: item.description || item.description2 || 'CDE Equipment',
              status: item.verification_type || 'verified',
              isVerified: Number(item.ISVerified) || 0,
              verificationStatus: item.verification_status || ''
            };
          });
          setCdeUnits(units);
        }
      } catch (error) {
        console.error('Error loading CDE units:', error);
      }
    };
    loadCDEUnits();
  }, []);

  // Filter to only not-verified CDEs
  const unverifiedCdes = cdeUnits.filter(cde => cde.isVerified !== 1);

  // Auto-slide CDE carousel
  useEffect(() => {
    if (unverifiedCdes.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % unverifiedCdes.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [unverifiedCdes.length]);

  const handleCDEClick = (cde: CDEUnit) => {
    // Store serial number and navigate to CDE verification form
    localStorage.setItem('serialNumber', cde.serialNumber);
    navigate('/cde-verification');
  };

  const loadRecentTickets = async () => {
    try {
      setLoading(true);
      const response = await ticketService.getRecentTickets(); // Get 3 most recent tickets

      if (response.success && response.tickets) {
        setRecentTickets(response.tickets);
      }
    } catch (error: any) {
      console.error('Error loading recent tickets:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to load recent tickets'
      });
    } finally {
      setLoading(false);
    }
  };


  const handleShowDuplicates = (ticket: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (ticket.duplicateCount > 1) {
      setDuplicateTicketsModal({
        isVisible: true,
        tickets: ticket.allDuplicates || [],
        ticketNumber: ticket.id
      });
    }
  };

  const handleMenuClick = (menuItem: string) => {
    switch(menuItem) {
      case 'raise-service-request':
        navigate('/raise-service-request');
        break;
      case 'raise-complaints':
        navigate('/dynamic-form/Raise Concern');
        break;
      case 'confirm-complaints-resolution':
        navigate('/my-complaints');
        break;
      case 'verify-cde-unit':
        navigate('/verify-cde');
        break;
      case 'view-product-information':
        navigate('/product-info');
        break;
      case 'scan-green-tag':
        navigate('/');
        break;
      case 'access-customer-training':
        navigate('/training');
        break;
      case 'tickets-history':
        navigate('/tickets-history');
        break;
      default:
        setAlert({
          isVisible: true,
          type: 'info',
          title: `${menuItem}`,
          message: `${menuItem} feature is coming soon! We're working to bring you this functionality.`
        });
    }
  };

  const menuItems = [
    {
      id: 'raise-service-request',
      title: 'Raise Service Request',
      icon: FaTools,
      description: 'Submit new service requests',
      isDemo: false
    },
    {
      id: 'confirm-complaints-resolution',
      title: 'My Complaints',
      icon: FaCheckCircle,
      iconImage: RaiseComplaintIcon,
      description: 'View and manage your complaints',
      isDemo: false
    },
    {
      id: 'verify-cde-unit',
      title: 'My Coca-Cola CDE',
      icon: FaSearch,
      description: 'Verify your CDE information',
      isDemo: false
    },
    {
      id: 'view-product-information',
      title: 'Product And Promotion',
      icon: FaInfo,
      description: 'Product catalogs and media',
      isDemo: false
    },
    {
      id: 'access-customer-training',
      title: 'Customer Training',
      icon: FaChartBar,
      description: 'Training modules and content',
      isDemo: false
    },
    {
      id: 'tickets-history',
      title: 'Tickets History',
      icon: FaHistory,
      description: 'View your service history',
      isDemo: false
    }
  ];

  // Transform recent tickets for display
  const allDisplayTickets = recentTickets.map(ticket => {
    const ticketStatus = ticket.status || ticket.form_status || 'Unknown';
    return {
      id: `#${ticket.form_number || ticket.uid?.substring(0, 8) || 'N/A'}`,
      uid: ticket.uid, // Keep ticket UID
      formUid: ticket.form_uid, // CRITICAL: Form UID for history API
      type: ticket.formName || ticket.form_name || 'Service Request',
      status: ticketStatus,
      actualStatus: ticketStatus, // Keep actual status for tracking
      date: new Date(ticket.createdTime || ticket.created_time).toLocaleDateString(),
      customer: ticket.customer_name,
      assignedRole: ticket.assignedRole || 'Not Assigned',
      assignedTo: ticket.assigned_to || ticket.assignedTo || '' // Add login ID
    };
  });

  // Group tickets by ticket number and keep only first occurrence with count
  const ticketNumberMap = new Map<string, { ticket: any; count: number; allTickets: any[] }>();

  allDisplayTickets.forEach(ticket => {
    const existing = ticketNumberMap.get(ticket.id);
    if (existing) {
      existing.count++;
      existing.allTickets.push(ticket);
    } else {
      ticketNumberMap.set(ticket.id, {
        ticket: ticket,
        count: 1,
        allTickets: [ticket]
      });
    }
  });

  // Convert map to array with count information and take only 3
  const displayTickets = Array.from(ticketNumberMap.values())
    .map(({ ticket, count, allTickets }) => ({
      ...ticket,
      duplicateCount: count,
      allDuplicates: allTickets
    }))
    .slice(0, 3);

  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} showHomeButton={false} showScannerButton={true} />
      <ContentContainer>

      <MenuGrid>
        {menuItems.map((item) => (
          <MenuItem key={item.id} onClick={() => handleMenuClick(item.id)}>
            {item.isDemo && <DemoBadge>Demo</DemoBadge>}
            <MenuIcon>
              {item.iconImage ? (
                <MenuIconImage src={item.iconImage} alt={item.title} />
              ) : (
                React.createElement(item.icon as any, {})
              )}
            </MenuIcon>
            <MenuTitle>{item.title}</MenuTitle>
          </MenuItem>
        ))}
      </MenuGrid>

      {/* Verify CDE Slider - only shows not-verified CDEs */}
      {unverifiedCdes.length > 0 && (
        <SliderSection>
          <SliderHeader>
            <SliderTitle>
              {React.createElement(FaSearch, {})}
              Verify Your CDE
            </SliderTitle>
            <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
              {currentSlide + 1} / {unverifiedCdes.length}
            </span>
          </SliderHeader>

          <SliderWrapper>
            <SliderTrack offset={currentSlide}>
              {unverifiedCdes.map((cde) => (
                <SliderCard key={cde.id} onClick={() => handleCDEClick(cde)}>
                  <SliderCDEImage src={cde.cdeImage} alt={cde.type} />
                  <SliderCDEInfo>
                    <SliderCDEType>{cde.type}</SliderCDEType>
                    <SliderCDEDesc>{cde.description}</SliderCDEDesc>
                    <SliderCDESerial>Serial: {cde.serialNumber}</SliderCDESerial>
                    {cde.model && <SliderCDESerial>Model: {cde.model}</SliderCDESerial>}
                    <SliderBadge verificationStatus={cde.isVerified}>
                      {cde.isVerified === 2 ? (
                        <>{React.createElement(FaClock, {})} Under Verification</>
                      ) : (
                        <>{React.createElement(FaTimesCircle, {})} Not Verified</>
                      )}
                    </SliderBadge>
                  </SliderCDEInfo>
                  <div style={{ color: '#dc3545', fontSize: '1.2rem', flexShrink: 0 }}>
                    {React.createElement(FaArrowRight, {})}
                  </div>
                </SliderCard>
              ))}
            </SliderTrack>
          </SliderWrapper>

          {unverifiedCdes.length > 1 && (
            <SliderNav>
              <SliderArrowBtn
                onClick={() => setCurrentSlide(prev => prev > 0 ? prev - 1 : unverifiedCdes.length - 1)}
              >
                {React.createElement(FaChevronLeft, {})}
              </SliderArrowBtn>
              {unverifiedCdes.map((_, i) => (
                <SliderDot key={i} active={i === currentSlide} onClick={() => setCurrentSlide(i)} />
              ))}
              <SliderArrowBtn
                onClick={() => setCurrentSlide(prev => (prev + 1) % unverifiedCdes.length)}
              >
                {React.createElement(FaChevronRight, {})}
              </SliderArrowBtn>
            </SliderNav>
          )}
        </SliderSection>
      )}

      <TicketHistoryPreview>
        <SectionTitle>
          {React.createElement(FaHistory as any, {})}
          Recent Tickets
        </SectionTitle>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>
            Loading tickets...
          </div>
        ) : displayTickets.length > 0 ? displayTickets.map((ticket) => (
          <TicketItem
            key={ticket.id}
            status={ticket.status}
          >
            <TicketInfo onClick={() => {
              setStatusTrackingPopup({
                isVisible: true,
                formName: ticket.type,
                currentStatus: ticket.status,
                ticketNumber: ticket.id
              });
            }} style={{ cursor: 'pointer' }}>
              <TicketId>
                {ticket.id}
              </TicketId>
              <TicketType>{ticket.type}</TicketType>
              {ticket.customer && (
                <TicketType style={{ fontSize: '0.75rem', marginTop: '0.1rem' }}>
                  Customer: {ticket.customer}
                </TicketType>
              )}
            </TicketInfo>
            <TicketActions>
              <TicketStatus status={ticket.status}>{ticket.status}</TicketStatus>
            </TicketActions>
          </TicketItem>
        )) : (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>
            No recent tickets found
          </div>
        )}
      </TicketHistoryPreview>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ isVisible: false, message: '' })}
        onConfirm={alert.onConfirm}
      />

      <StatusTrackingPopup
        isVisible={statusTrackingPopup.isVisible}
        formName={statusTrackingPopup.formName}
        currentStatus={statusTrackingPopup.currentStatus}
        ticketNumber={statusTrackingPopup.ticketNumber}
        onClose={() => setStatusTrackingPopup({ isVisible: false, formName: '', currentStatus: '', ticketNumber: '' })}
      />

      <TrackingHistoryModal
        isOpen={trackingHistoryModal.isVisible}
        onClose={() => setTrackingHistoryModal({ isVisible: false, recordUid: '', ticketNumber: '' })}
        recordUid={trackingHistoryModal.recordUid}
        ticketNumber={trackingHistoryModal.ticketNumber}
      />

      <DuplicatesModal
        isVisible={duplicateTicketsModal.isVisible}
        onClick={() => setDuplicateTicketsModal({ isVisible: false, tickets: [], ticketNumber: '' })}
      >
        <DuplicatesContainer onClick={(e) => e.stopPropagation()}>
          <DuplicatesHeader>
            <DuplicatesTitle>
              Duplicate Tickets - {duplicateTicketsModal.ticketNumber}
            </DuplicatesTitle>
            <DuplicatesCloseButton onClick={() => setDuplicateTicketsModal({ isVisible: false, tickets: [], ticketNumber: '' })}>
              {React.createElement(FaTimes, {})}
            </DuplicatesCloseButton>
          </DuplicatesHeader>
          <DuplicatesBody>
            {duplicateTicketsModal.tickets.map((ticket, index) => (
              <DuplicateItem
                key={ticket.uid}
                onClick={() => {
                  setDuplicateTicketsModal({ isVisible: false, tickets: [], ticketNumber: '' });
                  setTrackingHistoryModal({
                    isVisible: true,
                    recordUid: ticket.formUid || ticket.uid,
                    ticketNumber: ticket.id
                  });
                }}
              >
                <DuplicateItemHeader>
                  <div>
                    <DuplicateItemTitle>Instance #{index + 1}</DuplicateItemTitle>
                    <DuplicateItemDate>{ticket.date}</DuplicateItemDate>
                  </div>
                  <TicketStatus status={ticket.status}>{ticket.status}</TicketStatus>
                </DuplicateItemHeader>
                <DuplicateItemType>{ticket.type}</DuplicateItemType>
                {ticket.assignedRole && (
                  <DuplicateItemType>Assigned to: {ticket.assignedRole}</DuplicateItemType>
                )}
              </DuplicateItem>
            ))}
          </DuplicatesBody>
        </DuplicatesContainer>
      </DuplicatesModal>

      </ContentContainer>
    </PageContainer>
  );
};

export default CustomerDashboard;